import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { MagnifyingGlass } from "@phosphor-icons/react"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Form, FormField } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { BetterDocLookupResults } from "@/modules/admin/better-doc/components/better-doc-lookup-results"
import { LOOKUP_IDENTIFIER_MAXIMUM_LENGTH } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { useBetterDocFetch } from "@/modules/admin/better-doc/hooks/useBetterDocFetch"
import {
  BetterDocLookupSchema,
  betterDocLookupSchema,
} from "@/modules/admin/better-doc/schema/better-doc-lookup-schema"
import { fetchBetterDocLookup } from "@/modules/admin/better-doc/services/betterDocLookupQuery"

type BetterDocLookupRequest = {
  identifier: string
}

const fetchBetterDocLookupForRequest = ({ identifier }: BetterDocLookupRequest) => fetchBetterDocLookup(identifier)

export const BetterDocLookup = () => {
  const { t } = useTranslation()
  const [lookupRequest, setLookupRequest] = useState<BetterDocLookupRequest | null>(null)
  const lookupState = useBetterDocFetch(lookupRequest, fetchBetterDocLookupForRequest)

  const form = useForm<BetterDocLookupSchema>({
    resolver: zodResolver(betterDocLookupSchema),
    mode: "onChange",
    defaultValues: { identifier: "" },
  })
  const { control, handleSubmit, formState } = form

  const submitLookup = ({ identifier }: BetterDocLookupSchema) => {
    setLookupRequest({ identifier })
  }

  const renderLookupState = () => {
    switch (lookupState.status) {
      case "idle":
        return null
      case "loading":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-lookup-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        )
      case "loaded":
        return <BetterDocLookupResults result={lookupState.data} />
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="better-doc-admin-lookup">
      <Form {...form}>
        <form onSubmit={handleSubmit(submitLookup)} className="flex items-start gap-4">
          <div className="max-w-xl flex-1">
            <FormField
              control={control}
              name="identifier"
              render={({ field, fieldState }) => (
                <Input
                  label={t("admin.betterDoc.lookup.identifierLabel")}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  assistiveText={t("admin.betterDoc.lookup.identifierHint")}
                  error={
                    fieldState.error?.message
                      ? t(fieldState.error.message, { max: LOOKUP_IDENTIFIER_MAXIMUM_LENGTH })
                      : undefined
                  }
                  testId="better-doc-admin-lookup-identifier"
                />
              )}
            />
          </div>
          <Button
            type="submit"
            disabled={!formState.isValid || lookupState.status === "loading"}
            data-testid="better-doc-admin-lookup-submit">
            <MagnifyingGlass className="size-4" />
            {t("admin.betterDoc.lookup.search")}
          </Button>
        </form>
      </Form>
      {renderLookupState()}
    </div>
  )
}
